import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Mail, MapPin, Music2, MessageCircle, Phone } from 'lucide-react';
import { api } from '../lib/api';
import type { SiteSettings } from '../types';

const whatsappHref = (w: string) => (w.startsWith('http') ? w : `https://wa.me/${w.replace(/[^+\d]/g, '')}`);

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [settings, setSettings] = useState<SiteSettings>({});
  const whatsapp = settings.whatsapp ? whatsappHref(settings.whatsapp) : null;
  const nugobyteLink = 'https://wa.me/2349014010479';

  useEffect(() => {
    api.get<SiteSettings>('/settings').then(setSettings).catch(() => {});
  }, []);

  const linkClass = 'hover:text-primary transition-colors';

  return (
    <footer className="bg-obsidian text-white pt-12 border-t border-primary/40">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-[13px]">
          {/* Contact Us */}
          <div>
            <h3 className="font-semibold mb-5 text-primary tracking-widest text-sm capitalize">Contact Us</h3>
            <ul className="space-y-3">
              {settings.address && (
                <li className="flex items-start">
                  <MapPin className="w-4 h-4 mr-2 mt-0.5 text-primary shrink-0" />
                  <span className="tracking-wide text-taupe">{settings.address}</span>
                </li>
              )}
              {settings.phone && (
                <li className="flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-primary shrink-0" />
                  <a href={`tel:${settings.phone.replace(/[^+\d]/g, '')}`} className="tracking-wide text-taupe hover:text-primary">
                    {settings.phone}
                  </a>
                </li>
              )}
              {settings.email && (
                <li className="flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-primary shrink-0" />
                  <a href={`mailto:${settings.email}`} className="tracking-wide text-taupe hover:text-primary lowercase">
                    {settings.email}
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Quick Links */}
          <div className="lg:border-l lg:border-primary/25 lg:pl-8 relative">
            <span className="hidden lg:block absolute -left-[5px] top-0 w-2.5 h-2.5 bg-primary rotate-45" />
            <h3 className="font-semibold mb-5 text-primary tracking-widest text-sm capitalize">Quick Links</h3>
            <ul className="space-y-2.5">
              <li><Link to="/about" className={`text-taupe ${linkClass}`}>About Us</Link></li>
              <li><Link to="/blog" className={`text-taupe ${linkClass}`}>Blog</Link></li>
              <li><Link to="/contact" className={`text-taupe ${linkClass}`}>Contact Us</Link></li>
              <li><Link to="/sales" className={`text-taupe ${linkClass}`}>Sales</Link></li>
              <li><Link to="/leasing" className={`text-taupe ${linkClass}`}>Leasing</Link></li>
              <li><Link to="/management" className={`text-taupe ${linkClass}`}>Management</Link></li>
            </ul>
          </div>

          {/* Connect With Us */}
          <div className="lg:border-l lg:border-primary/25 lg:pl-8 relative">
            <span className="hidden lg:block absolute -left-[5px] top-0 w-2.5 h-2.5 bg-primary rotate-45" />
            <h3 className="font-semibold mb-5 text-primary tracking-widest text-sm capitalize">Connect With Us</h3>
            <ul className="space-y-2.5">
              {settings.facebook && (
                <li>
                  <a href={settings.facebook} target="_blank" rel="noopener noreferrer" className={`flex items-center text-taupe ${linkClass}`}>
                    <Facebook className="w-4 h-4 mr-2 text-primary shrink-0" /> Facebook
                  </a>
                </li>
              )}
              {settings.instagram && (
                <li>
                  <a href={settings.instagram} target="_blank" rel="noopener noreferrer" className={`flex items-center text-taupe ${linkClass}`}>
                    <Instagram className="w-4 h-4 mr-2 text-primary shrink-0" /> Instagram
                  </a>
                </li>
              )}
              {settings.linkedin && (
                <li>
                  <a href={settings.linkedin} target="_blank" rel="noopener noreferrer" className={`flex items-center text-taupe ${linkClass}`}>
                    <Linkedin className="w-4 h-4 mr-2 text-primary shrink-0" /> LinkedIn
                  </a>
                </li>
              )}
              {settings.tiktok && (
                <li>
                  <a href={settings.tiktok} target="_blank" rel="noopener noreferrer" className={`flex items-center text-taupe ${linkClass}`}>
                    <Music2 className="w-4 h-4 mr-2 text-primary shrink-0" /> TikTok
                  </a>
                </li>
              )}
              {whatsapp && (
                <li>
                  <a href={whatsapp} target="_blank" rel="noopener noreferrer" className={`flex items-center text-taupe ${linkClass}`}>
                    <MessageCircle className="w-4 h-4 mr-2 text-primary shrink-0" /> WhatsApp
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Artistic divider */}
        <div className="flex items-center justify-center gap-2 pt-12 pb-8">
          <div className="h-px w-20 bg-primary/40" />
          <div className="w-1.5 h-1.5 bg-primary/60 rotate-45" />
          <div className="w-2.5 h-2.5 bg-primary rotate-45" />
          <div className="w-1.5 h-1.5 bg-primary/60 rotate-45" />
          <div className="h-px w-20 bg-primary/40" />
        </div>
      </div>

      {/* Footer Bottom - ivory copyright bar */}
      <div className="bg-ivory">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col md:flex-row items-center justify-between gap-2 text-black text-[11px] md:text-xs">
          <p className="uppercase tracking-[0.25em]">
            © {currentYear} {settings.company_name}
          </p>
          {nugobyteLink ? (
            <a
              href={nugobyteLink}
              target="_blank"
              rel="noopener noreferrer"
              className="uppercase tracking-[0.25em] text-black hover:text-primary transition-colors"
            >
              All Rights Reserved
            </a>
          ) : (
            <span className="uppercase tracking-[0.25em] text-black">
              All Rights Reserved
            </span>
          )}
        </div>
      </div>
    </footer>
  );
}
